import { c as _c } from "react/compiler-runtime";
import { ClockIcon, CoinsHandIcon, RefreshCcw01Icon, Repeat03Icon, SlashCircle01Icon, UserPlus01Icon, type IconProps } from '@systemeio/icons';
import { CurrencyEnum } from '@systemeio/types';
import { cents, centsToUnits } from '@systemeio/util';
import { AffiliateCommissionTypeEnum } from 'modules/affiliate/enums/affiliate-commission-type-enum';
import { type ComponentType } from 'react';
import { Hint } from 'shared/components/hint';
import { Tooltip } from 'shared/components/tooltip';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format';
import { twJoin } from 'tailwind-merge';

/*
 * The fields both payment lists return, plus the dashboard's `isFirstPayment`, so each page passes
 * its own rows without mapping them.
 */
interface AffiliateCommissionCellPayment {
  amount: number;
  estimatedPayoutDate: string | null;
  isFirstPayment?: boolean;
  affiliateCommission: {
    affiliateCommission: number;
    type: AffiliateCommissionTypeEnum;
    cancelledAt?: string;
    offer: {
      payoutDelay: number;
    };
  };
  payment: {
    currency: CurrencyEnum;
    pending: boolean;
    refundedAt?: string;
  };
}
type StatusIconTone = 'accent' | 'error' | 'warning';
const statusIconToneClassName: Record<StatusIconTone, string> = {
  accent: 'data-popup-open:text-icon-accent',
  error: 'data-popup-open:text-icon-error',
  warning: 'data-popup-open:text-icon-warning'
};
const PaymentStatusIcon = t0 => {
  const $ = _c(8);
  const {
    icon: Icon,
    label,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "accent" : t1;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon className="size-5" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = statusIconToneClassName[tone];
  let t4;
  if ($[2] !== t3) {
    t4 = twJoin("text-icon-tertiary", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label || $[5] !== t2 || $[6] !== t4) {
    t5 = <Hint label={label} trigger={t2} triggerClassName={t4} />;
    $[4] = label;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
export const AffiliateCommissionCell = t0 => {
  const $ = _c(46);
  const {
    payment
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== payment.affiliateCommission.affiliateCommission || $[1] !== payment.amount || $[2] !== payment.payment.currency || $[3] !== user?.dashboardLocale) {
    t1 = currencyFormat(centsToUnits(cents(payment.amount * (payment.affiliateCommission.affiliateCommission / 100))), payment.payment.currency, user?.dashboardLocale);
    $[0] = payment.affiliateCommission.affiliateCommission;
    $[1] = payment.amount;
    $[2] = payment.payment.currency;
    $[3] = user?.dashboardLocale;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <span className="text-text-primary truncate">{t1}</span>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const commission = t2;
  let t3;
  if ($[7] !== commission || $[8] !== payment.affiliateCommission.offer || $[9] !== payment.affiliateCommission.type || $[10] !== payment.estimatedPayoutDate || $[11] !== t || $[12] !== user?.dateFormat || $[13] !== user?.timezone) {
    t3 = payment.affiliateCommission.type === AffiliateCommissionTypeEnum.first_tier && payment.estimatedPayoutDate ? <Tooltip mode="hover" label={<div className="text-start"><TransLoco t={t} i18nKey="dashboard.affiliate.commission_tooltip" values={{
        delay: payment.affiliateCommission.offer.payoutDelay,
        date: dateFormat(payment.estimatedPayoutDate, {
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })
      }} /></div>}>{commission}</Tooltip> : commission;
    $[7] = commission;
    $[8] = payment.affiliateCommission.offer;
    $[9] = payment.affiliateCommission.type;
    $[10] = payment.estimatedPayoutDate;
    $[11] = t;
    $[12] = user?.dateFormat;
    $[13] = user?.timezone;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== payment.isFirstPayment || $[16] !== t) {
    t4 = payment.isFirstPayment && <PaymentStatusIcon icon={Repeat03Icon} label={t("dashboard.affiliate.first_payment")} />;
    $[15] = payment.isFirstPayment;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== payment.payment.refundedAt || $[19] !== t || $[20] !== user?.dateFormat || $[21] !== user?.timeFormat || $[22] !== user?.timezone) {
    t5 = payment.payment.refundedAt && <PaymentStatusIcon icon={RefreshCcw01Icon} tone="error" label={t("dashboard.affiliate-program.refunded", {
      date: dateTimeFormat(payment.payment.refundedAt, {
        dateFormat: user?.dateFormat,
        timeFormat: user?.timeFormat,
        timeZone: user?.timezone
      })
    })} />;
    $[18] = payment.payment.refundedAt;
    $[19] = t;
    $[20] = user?.dateFormat;
    $[21] = user?.timeFormat;
    $[22] = user?.timezone;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== payment.affiliateCommission.cancelledAt || $[25] !== t) {
    t6 = payment.affiliateCommission.cancelledAt && <PaymentStatusIcon icon={SlashCircle01Icon} tone="error" label={t("dashboard.affiliate-program.commission_revoked_tooltip")} />;
    $[24] = payment.affiliateCommission.cancelledAt;
    $[25] = t;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== payment.payment.pending || $[28] !== t) {
    t7 = payment.payment.pending && <PaymentStatusIcon icon={ClockIcon} tone="warning" label={t("dashboard.affiliate.pending")} />;
    $[27] = payment.payment.pending;
    $[28] = t;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== payment.affiliateCommission.type || $[31] !== t) {
    t8 = payment.affiliateCommission.type === AffiliateCommissionTypeEnum.next_gen && <PaymentStatusIcon icon={CoinsHandIcon} label={t("dashboard.affiliate.next_gen_commission")} />;
    $[30] = payment.affiliateCommission.type;
    $[31] = t;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== payment.affiliateCommission.type || $[34] !== t) {
    t9 = payment.affiliateCommission.type === AffiliateCommissionTypeEnum.second_tier && <PaymentStatusIcon icon={UserPlus01Icon} label={t("dashboard.affiliate.second_tier_commission")} />;
    $[33] = payment.affiliateCommission.type;
    $[34] = t;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t10 = <div className="flex items-center gap-1.5">{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t3) {
    t11 = <div className="max-desktop:flex-row-reverse flex flex-wrap items-center gap-3">{t3}{t10}</div>;
    $[43] = t10;
    $[44] = t3;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
};